import { useEffect, useMemo, useRef, useState } from 'react';
import { Paper, Progress, Stack, Text } from '@mantine/core';
import { SubstitutionGenerationProgress } from '../../types/mealPlan';
import {
  PlanHousehold,
  WAITING_LINE_SECONDS,
  hashSeed,
  linesFor,
  shuffledLines,
} from '../../utils/waitingLines';
import styles from './StreamProgress.module.css';

type Props = {
  // No status prop on purpose: the card only renders this once a progress
  // snapshot exists, and the snapshot carries everything the copy needs. The
  // plan state's substitutionStatus can lag the stream (it is only patched on
  // structural refetches), so deriving from it would freeze the early phase.
  progress: SubstitutionGenerationProgress;
  /**
   * Household the plan was built for, from its profile snapshot. Picks which
   * waiting lines make sense, the same way the plan generation banner does.
   */
  household?: PlanHousehold;
  /** Meal being swapped; breakfast/lunch drop the dinner-flavored lines. */
  mealType?: 'breakfast' | 'lunch' | 'dinner';
};

/**
 * Not the plan banner's "a minute or two": that opener was calibrated on
 * whole-plan generation, and a substitution is one assignment on a faster
 * model. Measured over a month of production swaps (Aug 2026, 59 AI-path
 * tasks): ~50s median before the model change, ~33s median / 54s max in the
 * week after it. Saying "about" rather than a number leaves room for the
 * tail without reading as a promise.
 */
const OPENING_LINE = 'This usually takes about half a minute';

/**
 * When to stop claiming this is normal. Recent production swaps run ~33s
 * median / ~54s max (Aug 2026), and the server's corrective retry can
 * legitimately double a run, so escalation waits until past the
 * retry-doubled case rather than the single-run one.
 */
const SLOW_AFTER_SECONDS = 90;

/** How often the waiting copy re-checks the clock. */
const CLOCK_INTERVAL_MS = 10_000;

/**
 * Where the bar sits, by phase.
 *
 * A substitution writes one recipe, so there is no item count to carry the
 * bar: reasoning volume moves it until the model starts writing, then the
 * recipe's ingredients and steps completing carry it the rest of the way.
 * Capped short of full because the finish signal is substitutionStatus
 * flipping to completed, never the bar itself.
 */
const THINKING_FLOOR = 6;
const WRITING_FLOOR = 30;
const WRITING_CEILING = 95;

/**
 * Asymptotic like the plan bar's planning stage, and for the same reason: the
 * total reasoning is unknowable ahead of time. A substitution reasons over a
 * single dish, so the scale sits below the grocery bar's 2500; not yet
 * measured in production, seeded from the neighboring flows.
 */
const THINKING_SCALE_CHARS = 1800;

/**
 * Ingredients plus steps completed so far, against an asymptotic curve
 * because the recipe's true size is unknown: a typical dinner lands around
 * ten ingredients and six steps, which this scale carries to ~85% of the
 * writing range.
 */
const PARTS_SCALE = 8;

/** A cookbook pick is decided the moment it streams; only scaling and the
 * save remain, so the bar parks just short of the cap. */
const COOKBOOK_PICK_PERCENT = 90;

const barPercent = (progress: SubstitutionGenerationProgress): number => {
  if (progress.cookbookPick) return COOKBOOK_PICK_PERCENT;

  if (!progress.outputStarted) {
    return (
      THINKING_FLOOR +
      (WRITING_FLOOR - THINKING_FLOOR) *
        (1 - Math.exp(-(progress.thinkingChars ?? 0) / THINKING_SCALE_CHARS))
    );
  }

  const parts = progress.ingredientsSoFar + progress.stepsSoFar;
  return (
    WRITING_FLOOR +
    (WRITING_CEILING - WRITING_FLOOR) * (1 - Math.exp(-parts / PARTS_SCALE))
  );
};

const statusLabel = (progress: SubstitutionGenerationProgress): string => {
  const hasContent =
    !!progress.recipeName ||
    progress.cookbookPick ||
    progress.ingredientsSoFar > 0;

  // A restart is worth saying out loud, otherwise the bar emptying reads as
  // progress being lost.
  if (progress.attempt > 1 && !hasContent) {
    return 'Giving it another go';
  }

  // Written from the kitchen's side: what a person reworking your week would
  // say they were doing. Nothing narrates how it is produced.
  if (!progress.outputStarted) return 'Finding a new dish';
  if (progress.cookbookPick) return 'Pulling one from your cookbook';
  if (!progress.recipeName) return 'Settling on a dish';
  return 'Writing up the recipe';
};

/**
 * Shows a recipe substitution taking shape while the replacement is still
 * being written. Fills the overlay the bare spinner used to occupy: a bar
 * that moves through the reasoning stretch, the rotating waiting copy the
 * plan generation banner uses, and the new recipe's name the moment it is
 * decided — the reveal the card is waiting for.
 */
export const SubstitutionProgress = ({ progress, household, mealType }: Props) => {
  // The wait is measured from when the overlay appeared, which is within a
  // beat of the request. The same instant seeds the line shuffle, so the
  // order is stable across re-renders but differs per swap.
  const startedAtRef = useRef(Date.now());
  const [now, setNow] = useState(() => Date.now());

  // Until the name lands, the detail line carries the waiting copy.
  // Re-checked on a timer because the thinking stretch can pass several
  // seconds without a progress write to re-render on.
  const waiting = !progress.recipeName;
  useEffect(() => {
    if (!waiting) return;
    const id = setInterval(() => setNow(Date.now()), CLOCK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [waiting]);

  // Shuffled once per swap, not per render, so the line does not jump around
  // between renders that happen inside the same ten second slot.
  const lineOrder = useMemo(
    () =>
      shuffledLines(linesFor(household, mealType), hashSeed(String(startedAtRef.current))),
    [household, mealType]
  );

  const elapsedSeconds = (now - startedAtRef.current) / 1000;
  const runningLong = elapsedSeconds > SLOW_AFTER_SECONDS;

  const waitingText = runningLong
    ? // Stays plain. Someone minutes into a wait is not in the mood to be
      // charmed, and a joke here would read as not taking it seriously.
      'Hang tight, this is taking longer than usual'
    : // The expectation lands first, then it cycles.
      (() => {
        const slot = Math.max(
          0,
          Math.floor(elapsedSeconds / WAITING_LINE_SECONDS)
        );
        if (slot === 0) return OPENING_LINE;
        return lineOrder[(slot - 1) % lineOrder.length];
      })();

  return (
    // A solid plate rather than text straight on the overlay: the overlay is
    // translucent and the card's own content sits right behind it, so the
    // label — the dimmed recipe name especially — was unreadable against it.
    <Paper shadow="sm" radius="md" p="sm" withBorder w={230} maw="78vw">
      <Stack gap={6}>
        <Text size="xs" fw={500} lh={1.3} ta="center">
          {statusLabel(progress)}
        </Text>
        <Progress
          value={barPercent(progress)}
          size="xs"
          radius="xl"
          color="blue"
          classNames={{ section: styles.barFill }}
          transitionDuration={400}
        />
        <Text
          key={progress.recipeName ?? waitingText}
          size="xs"
          c="dimmed"
          lh={1.3}
          ta="center"
          // Two lines are reserved rather than one-line-with-ellipsis: names
          // wrap in full, and reserving the space up front keeps the plate's
          // height fixed from first render, so nothing shifts when a line
          // swaps or the name lands. Two lines at this width fits any
          // realistic recipe name and every waiting line.
          style={{ minHeight: '2.6em' }}
          className={styles.featuredItem}
        >
          {progress.recipeName ? (
            <Text component="span" fw={600} inherit>
              {progress.recipeName}
            </Text>
          ) : (
            waitingText
          )}
        </Text>
      </Stack>
    </Paper>
  );
};
